import { faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { SearchBar } from './SearchBar';

import 'view/style/common/components/itemSelector.css';
import FinePrint from './FinePrint';

interface DualColumnSelectorProps<T> {
  items: T[];
  selectedItems: T[];
  style?: React.CSSProperties;
  getId: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  filterItem: (items: T[], searchTerm: string) => T[];
  visibleFilter?: (item: T) => boolean;
  onRemoveItem: (item: T) => void;
  onAddItem: (item: T) => void;
}

function DualColumnSelector<T>({
  items,
  selectedItems,
  style,
  getId,
  renderItem,
  visibleFilter,
  filterItem,
  onRemoveItem,
  onAddItem,
}: DualColumnSelectorProps<T>) {
  const [available, setAvailable] = useState<T[]>(
    items.filter((i) => !selectedItems.find((it) => it === i)),
  );
  const [selected, setSelected] = useState<T[]>(selectedItems);
  const [availableSearchTerm, setAvailableSearchTerm] = useState<string>('');
  const [selectedSearchTerm, setSelectedSearchTerm] = useState<string>('');

  useEffect(() => {
    setAvailable(items.filter((i) => !selectedItems.find((it) => it === i)));
    setSelected(selectedItems);
  }, [items, selectedItems]);

  const displayAvailable = useMemo(() => {
    let result = available;
    if (availableSearchTerm) {
      result = filterItem(result, availableSearchTerm);
    }
    if (visibleFilter) {
      result = result.filter(visibleFilter);
    }
    return result;
  }, [available, availableSearchTerm, visibleFilter, filterItem]);

  const displaySelected = useMemo(() => {
    let result = selected;
    if (selectedSearchTerm) {
      result = filterItem(result, selectedSearchTerm);
    }
    if (visibleFilter) {
      result = result.filter(visibleFilter);
    }
    return result;
  }, [selected, selectedSearchTerm, visibleFilter, filterItem]);

  const handleSelectAll = () => {
    setSelected((prev) => [...prev, ...displayAvailable]);
    setAvailable([]);
    displayAvailable.forEach((item) => onAddItem(item));
  };

  const handleRemoveAll = () => {
    setAvailable((prev) => [...prev, ...displaySelected]);
    setSelected([]);
    displaySelected.forEach((item) => onRemoveItem(item));
  };

  return (
    <div className="item-selector" style={style}>
      {/* Available Column */}
      <div className="item-column">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <label className="label normal">Available</label>
          {displayAvailable.length ? (
            <button
              onClick={handleSelectAll}
              style={{ fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Select All
            </button>
          ) : null}
        </div>
        <SearchBar
          showDropdown={displayAvailable.length > 0}
          onSearch={(searchTerm: string) => setAvailableSearchTerm(searchTerm)}
        >
          {displayAvailable.map((item) => (
            <div
              key={`available-items-${getId(item)}`}
              onDoubleClick={() => onAddItem(item)}
              style={{ width: '100%' }}
            >
              {renderItem(item)}
            </div>
          ))}
        </SearchBar>
        <FinePrint text="Hint: double click an item to select/deselect it" />
      </div>

      {/* Selected Column */}
      <div className="item-column">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <label className="label normal">Selected</label>
          {displaySelected.length ? (
            <button
              onClick={handleRemoveAll}
              style={{ fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Remove All
            </button>
          ) : null}
        </div>
        <SearchBar
          showDropdown={displaySelected.length > 0}
          onSearch={(searchTerm: string) => setSelectedSearchTerm(searchTerm)}
        >
          {displaySelected.map((item) => (
            <div
              key={`selected-items-${getId(item)}`}
              onDoubleClick={() => onRemoveItem(item)}
              style={{ width: '100%' }}
            >
              <FontAwesomeIcon
                onClick={() => onRemoveItem(item)}
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--dark-pink)',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
                className=""
                aria-label="Remove item"
                icon={faXmarkCircle}
              />
              {renderItem(item)}
            </div>
          ))}
        </SearchBar>
      </div>
    </div>
  );
}

export default DualColumnSelector;
